import { createSlice } from '@reduxjs/toolkit'
import { policyViolationsInitialState as initialState } from '../initialState/policyViolationsState'
import { apiCallBegan } from '../api'

// The 3 internal-portal-pixm-fa endpoints below (PIXMC-6224) all use the
// { data: [...], errors: [] } envelope, but the list endpoint wraps a single object
// (data[0] = { totalCount, records }) while the two aggregate endpoints' data IS the
// array itself (data = [{ctrlType,count}, ...]) — firstOf/allOf pick the right shape.
const firstOf = (action) => {
  const body = action.payload.response
  const arr = body?.data ?? []
  return Array.isArray(arr) ? (arr[0] ?? null) : arr
}
const allOf = (action) => {
  const body = action.payload.response
  return Array.isArray(body?.data) ? body.data : []
}

function extractError(payload) {
  if (payload && payload.errors && payload.errors.length > 0) {
    return payload.errors[0].desc || payload.errors[0].message || 'Unknown error'
  }
  if (payload && payload.message) {
    return payload.message
  }
  if (typeof payload === 'string') {
    return payload
  }
  return 'Unknown error'
}

const slice = createSlice({
  name: 'policyViolations',
  initialState,
  reducers: {
    // ----- List -----
    policyViolationsRequested: (state) => {
      state.listLoading = true
      state.listError = null
    },
    policyViolationsReceived: (state, action) => {
      state.listLoading = false
      const item = firstOf(action)
      state.records = item?.records ?? []
      state.totalCount = item?.totalCount ?? 0
    },
    policyViolationsFailed: (state, action) => {
      state.listLoading = false
      state.listError = extractError(action.payload)
      state.records = []
      state.totalCount = 0
    },

    // ----- Count by control type (stat tiles) -----
    policyViolationsCountByTypeRequested: (state) => {
      state.countByTypeLoading = true
      state.countByTypeError = null
    },
    policyViolationsCountByTypeReceived: (state, action) => {
      state.countByTypeLoading = false
      state.countByType = allOf(action)
    },
    policyViolationsCountByTypeFailed: (state, action) => {
      state.countByTypeLoading = false
      state.countByTypeError = extractError(action.payload)
      state.countByType = []
    },

    // ----- Count by category -----
    policyViolationsCountByCategoryRequested: (state) => {
      state.countByCategoryLoading = true
      state.countByCategoryError = null
    },
    policyViolationsCountByCategoryReceived: (state, action) => {
      state.countByCategoryLoading = false
      state.countByCategory = allOf(action)
    },
    policyViolationsCountByCategoryFailed: (state, action) => {
      state.countByCategoryLoading = false
      state.countByCategoryError = extractError(action.payload)
      state.countByCategory = []
    },

    // ----- Filters (search, sort, sub-tab/controlType, date range, page) -----
    setPolicyViolationsFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetPolicyViolations: () => initialState,
  },
})

export const {
  policyViolationsRequested, policyViolationsReceived, policyViolationsFailed,
  policyViolationsCountByTypeRequested, policyViolationsCountByTypeReceived, policyViolationsCountByTypeFailed,
  policyViolationsCountByCategoryRequested, policyViolationsCountByCategoryReceived, policyViolationsCountByCategoryFailed,
  setPolicyViolationsFilters, resetPolicyViolations,
} = slice.actions

export default slice.reducer

// ===========================================================================
// Thunks — internal-portal-pixm-fa `policy-violations*` (customer-scoped, PIXMC-6224).
// ===========================================================================

// The two aggregate endpoints only understand the date-range params — trimming to just
// those (and only sending fromDate/toDate when dateRange is actually 'custom') keeps
// their query string minimal and mirrors the API's own cache-key rationale (page/sortBy
// are meaningless to an endpoint that ignores them).
const pickDateFilters = (filters = {}) => {
  const picked = { dateRange: filters.dateRange }
  if (filters.dateRange === 'custom') {
    picked.fromDate = filters.fromDate
    picked.toDate = filters.toDate
  }
  return picked
}

// GET the paginated/searchable/sortable list, filtered by the active sub-tab
// (filters.controlType) and date range.
export const getPolicyViolations = (customerId, filters) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/policy-violations`, method: 'GET', filters,
  onStart: policyViolationsRequested.type, onSuccess: policyViolationsReceived.type, onError: policyViolationsFailed.type,
})

// GET aggregate counts by control type (DATA|AI|WEB|EXTENSION) — feeds the 3 stat tiles.
export const getPolicyViolationsCountByType = (customerId, filters) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/policy-violations/count-by-type`, method: 'GET', filters: pickDateFilters(filters),
  onStart: policyViolationsCountByTypeRequested.type, onSuccess: policyViolationsCountByTypeReceived.type, onError: policyViolationsCountByTypeFailed.type,
})

// GET aggregate counts by category — fetched per the API contract; not yet visualized
// by a dedicated widget on this screen (only the sortable Category column uses it today).
export const getPolicyViolationsCountByCategory = (customerId, filters) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/policy-violations/count-by-category`, method: 'GET', filters: pickDateFilters(filters),
  onStart: policyViolationsCountByCategoryRequested.type, onSuccess: policyViolationsCountByCategoryReceived.type, onError: policyViolationsCountByCategoryFailed.type,
})
